import { CPLAT_API_ROUTES, usePost } from '@/api';
import GreenCheckIcon from '@/assets/icons/GreenCheckIcon.svg?react';
import { Dialog } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import Input from '@/components/V3/Input';
import { cn } from '@/utils/tailwind';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocalStorage } from 'usehooks-ts';
import z from 'zod';
import { UpdateAccountDialogContent } from './update-account-dialog-content';
import { addHyphenPhoneNumber, removeHyphen } from '@/utils/phone';

interface Props {
  verificationToken?: string;
  onChangeVerificationToken: (token: string) => void;
  onNext: () => void;
  isMypage?: boolean;
  compact?: boolean;
  onCanceled?: () => void;
}

const formSchema = z.object({
  phone: z.string(),
  verifyCode: z.string(),
});

type FormType = z.infer<typeof formSchema>;

export const ResetPasswordPhoneForm = ({
  verificationToken,
  onChangeVerificationToken,
  onNext,
  isMypage = false,
  compact = false,
  onCanceled,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [, setLocalStorageCplatToken] = useLocalStorage('cplatToken', '');

  const TOTAL_SECONDS = 3 * 60; // 3분 = 180초
  const [remaining, setRemaining] = useState(TOTAL_SECONDS);
  const endTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const [smsToken, setSmsToken] = useState<string>();

  const [buttonLabel, setButtonLabel] = useState('인증번호 발송');

  const [codeVerified, setCodeVerified] = useState(false);

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
  } = useForm<FormType>({ resolver: zodResolver(formSchema) });

  const watchedPhone = watch('phone');
  const watchedVerifyCode = watch('verifyCode');

  const { mutate: onVerifyCode } = usePost(
    CPLAT_API_ROUTES.verifyAuthenticationCode,
    undefined,
    undefined,
    true,
  );

  const update = useCallback(() => {
    if (!endTimeRef.current) return;

    const now = Date.now();
    const diff = Math.max(0, Math.ceil((endTimeRef.current - now) / 1000));

    setRemaining(diff);

    if (diff <= 0) {
      endTimeRef.current = null;
      cancelAnimationFrame(rafRef.current!);
      return;
    }

    rafRef.current = requestAnimationFrame(update);
  }, []);

  const restart = () => {
    cancelAnimationFrame(rafRef.current!);
    endTimeRef.current = Date.now() + TOTAL_SECONDS * 1000;
    setRemaining(TOTAL_SECONDS);
    rafRef.current = requestAnimationFrame(update);
  };

  const { mutate: onCheckPhone } = usePost(CPLAT_API_ROUTES.checkPhone, undefined, undefined, true);

  const { mutate: onSendVerifyCode } = usePost(
    CPLAT_API_ROUTES.sendAuthenticationCode,
    undefined,
    undefined,
    true,
  );

  const { mutate: onSigninByPhone } = usePost(
    CPLAT_API_ROUTES.signinByPhone,
    undefined,
    undefined,
    true,
  );

  const { mutate: getInfo } = usePost(CPLAT_API_ROUTES.getInfo, undefined, undefined, true);

  const handleSendVerifyCode = () => {
    onCheckPhone(
      { phoneNo: removeHyphen(watchedPhone) },
      {
        onSuccess: (response) => {
          const isRegistered = response.data.data[0].isRegistered;
          if (!isRegistered) setError('phone', { message: '입력한 번호로 가입한 이력이 없습니다' });
          else
            onSendVerifyCode(
              { phoneNo: removeHyphen(watchedPhone) },
              {
                onSuccess: (response) => {
                  clearErrors();
                  setButtonLabel('재발송');
                  restart();
                  setSmsToken(response.data.data[0].smsToken);
                },
              },
            );
        },
      },
    );
  };

  useEffect(() => {
    if (watchedVerifyCode?.length === 6) {
      onVerifyCode(
        { smsToken, smsCode: watchedVerifyCode },
        {
          onSuccess: (response) => {
            const verificationToken = response.data.data[0].verificationToken;
            onChangeVerificationToken(verificationToken);
            setCodeVerified(true);
          },
        },
      );
    }
  }, [onChangeVerificationToken, onVerifyCode, smsToken, watchedVerifyCode]);

  useEffect(() => {
    setButtonLabel('인증번호 발송');
    setCodeVerified(false);
  }, [watchedPhone]);

  useEffect(() => {
    if (remaining === 0) setError('verifyCode', { message: '입력시간이 초과되었습니다' });
  }, [remaining, setError]);

  const handlePhoneNoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^\d-]/g, '');
    const digits = removeHyphen(value);

    if (digits.length > 11) return;

    const isnNotIncludePrefixNum = digits.length >= 3 && digits.substring(0, 3) !== '010';

    if (isnNotIncludePrefixNum) {
      setError('phone', {
        type: 'pattern',
        message: '휴대폰 번호는 "010-" 으로 시작해야합니다.',
      });
    } else {
      setError('phone', {});
    }

    value = addHyphenPhoneNumber(digits);

    setValue('phone', value);
  };

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col justify-between max-md:justify-start max-md:gap-5',
        {
          'justify-start gap-5': isMypage || compact,
        },
      )}
    >
      <div className="w-full space-y-5">
        {!isMypage && (
          <div className="w-full space-y-3">
            <div className={cn('text-gr-800', compact ? 'Body1S16' : 'Title3S18')}>
              휴대폰 번호를 입력해 주세요
            </div>
            <div className={cn('text-gr-700', compact ? 'Body5M14' : 'Body2M16')}>
              비밀번호 재설정을 위해 가입에 사용한
              <br />
              휴대폰 번호를 입력해 주세요
            </div>
          </div>
        )}

        <div className="flex w-full flex-col gap-3">
          <div className="flex gap-2 ">
            <Input
              placeholder="휴대폰 번호를 입력해 주세요"
              className={cn(compact && 'Body5M14')}
              {...register('phone')}
              Error={{ isError: !!errors.phone?.message, errorMessage: errors.phone?.message }}
              onChange={handlePhoneNoChange}
            />
            <Button
              variant={isMypage ? 'line' : compact ? 'secondary' : 'default'}
              className={cn(
                compact &&
                  'w-[97px] shrink-0 rounded-[8px] border border-gr-200 disabled:bg-gr-200 disabled:text-wh',
              )}
              disabled={!watchedPhone || codeVerified || !!errors.phone?.message}
              onClick={handleSendVerifyCode}
            >
              {buttonLabel}
            </Button>
          </div>
          {buttonLabel === '재발송' && (
            <Input
              placeholder="인증번호를 입력해 주세요"
              InputProps={{
                endAdornment: codeVerified ? (
                  <GreenCheckIcon />
                ) : (
                  <span className={cn({ 'text-red-400': remaining === 0 })}>
                    {formatTime(remaining)}
                  </span>
                ),
                endAdornmentStyle: 'text-blue-400 Body5M14 right-2',
              }}
              disabled={codeVerified}
              Error={{
                isError: !!errors.verifyCode?.message,
                errorMessage: errors.verifyCode?.message,
              }}
              {...register('verifyCode')}
              maxLength={6}
              autoComplete="off"
            />
          )}
        </div>
      </div>
      {isMypage ? (
        <div className="flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={onCanceled}>
            취소
          </Button>
          <Button disabled={!codeVerified} className="flex-1" onClick={() => onNext()}>
            확인
          </Button>
        </div>
      ) : (
        <Button
          className={cn('w-full', compact && 'rounded-[8px] Body3S15')}
          disabled={!codeVerified}
          onClick={handleSubmit(
            () =>
              onSigninByPhone(
                { verificationToken },
                {
                  onSuccess: (response) => {
                    const cplatToken = response.data.data[0].cplatToken;

                    getInfo(
                      { cplatToken },
                      {
                        onSuccess: (response) => {
                          const email = response.data.data[0].accountEmail;
                          if (email) onNext();
                          else {
                            setLocalStorageCplatToken(cplatToken);
                            setOpen(true);
                          }
                        },
                      },
                    );
                  },
                },
              ),
            (erro) => console.log(erro),
          )}
        >
          다음
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <UpdateAccountDialogContent />
      </Dialog>
    </div>
  );
};

const formatTime = (seconds: number) => {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};
